import React, { useState } from "react";
import { HelpCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/context/I18nContext";
import { useHelp } from "@/context/HelpContext";
import { useAuth } from "@/context/AuthContext";
import { useLocation } from "react-router-dom";
import { getHelpEntry } from "@/lib/helpContent";

// Derive a default help key from the current route + user role when the
// current page hasn't explicitly registered one.
function defaultKey({ pathname, user }) {
  if (pathname === "/") return "landing";
  if (pathname === "/login") return "login";
  if (pathname === "/register") return "register";
  if (pathname === "/forgot-password") return "forgot_password";
  if (pathname === "/reset-password") return "reset_password";
  if (pathname.startsWith("/app")) {
    if (!user || !user.role) return "onboarding";
    if (user.role === "municipality") return "muni.overview";
    if (user.role === "utility") return "util.overview";
    return "owner.overview";
  }
  return null;
}

export default function HelpButton() {
  const { t, lang } = useI18n();
  const { helpKey } = useHelp();
  const { user } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const key = helpKey || defaultKey({ pathname: loc.pathname, user });
  const entry = getHelpEntry(key, lang);

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(true)}
        aria-label={t("help")}
        data-testid="help-btn"
      >
        <HelpCircle className="h-5 w-5" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="help-dialog">
          <DialogHeader>
            <DialogTitle data-testid="help-dialog-title">{entry.title}</DialogTitle>
            <DialogDescription className="sr-only">{t("help")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3" data-testid="help-dialog-body">
            {entry.body.map((p, i) => (
              <p key={i} className="text-sm leading-relaxed text-muted-foreground">
                {p}
              </p>
            ))}
          </div>
          <div className="mt-2 flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpen(false)}
              data-testid="help-close-btn"
            >
              {t("help_close")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
